/**
 * useSessionLog — this tab's active time and the local, append-only history of past sessions.
 *
 * Moved verbatim out of CommandDashboard (2026-10) so the dashboard can be about the market and the
 * system's decisions while this record keeps working exactly as before.
 */
import { useEffect, useRef, useState } from 'react';
import { setVisibleInterval } from '@/utils/visibleInterval';

/* ── Persistent session log (localStorage) ─────────────────────────────
 * The SESSION stat tile's live counter (below) deliberately uses
 * sessionStorage so it resets per tab — that's correct for "active time
 * this tab". But an operator watching that number has no way to see what
 * happened to *previous* sessions once a tab closes; they just vanish.
 *
 * This is a separate, append-only record kept in localStorage (which,
 * unlike sessionStorage, survives tab/browser close). Each session gets
 * one entry, written once at start and closed exactly once at the end —
 * entries are never edited after `endedAt` is set. This is a LOCAL,
 * PER-DEVICE record only: it lives in this browser's localStorage and is
 * not synced to any server. There is no backend audit-log endpoint in
 * this codebase (confirmed by search) — real server-side audit logging
 * would need one to be built; this does not pretend to be that.
 */
export interface SessionLogEntry {
  id: string;
  startedAt: string;        // ISO timestamp, set once
  endedAt: string | null;   // ISO timestamp, written exactly once, then immutable
}

const SESSION_LOG_KEY = 'aurexis_session_log';
export const SESSION_LOG_MAX = 200; // cap so localStorage can't grow unbounded; oldest entries drop first

function readSessionLog(): SessionLogEntry[] {
  try {
    const raw = localStorage.getItem(SESSION_LOG_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeSessionLog(log: SessionLogEntry[]) {
  try {
    // Trim from the front (oldest first). Entries with a real endedAt are
    // still just dropped, never rewritten — trimming removes whole entries,
    // it doesn't touch the fields of the ones that remain.
    localStorage.setItem(SESSION_LOG_KEY, JSON.stringify(log.slice(-SESSION_LOG_MAX)));
  } catch {
    // localStorage unavailable or full — the history log is best-effort;
    // the live "active time" counter above is unaffected either way.
  }
}

export function useSessionLog() {
  // sessionStorage, not localStorage: this must be THIS session's active time —
  // localStorage never expires, so after a day the widget would still read
  // "active time" from a session that ended hours ago. sessionStorage clears
  // when the tab closes, which is the actual definition of "this session".
  const SESSION_START_KEY = 'aurexis_session_start';
  const [sessSecs, setSessSecs] = useState(() => {
    const stored = sessionStorage.getItem(SESSION_START_KEY);
    if (stored) {
      const diff = Math.floor((Date.now() - Number(stored)) / 1000);
      return diff > 0 ? diff : 0;
    }
    sessionStorage.setItem(SESSION_START_KEY, String(Date.now()));
    return 0;
  });

  // Ties this browser tab to exactly one persistent session-log entry.
  // sessionStorage (not localStorage) so it survives this component
  // remounting on in-app navigation, but still resets to nothing when the
  // tab actually closes — same lifetime as SESSION_START_KEY above.
  const SESSION_ID_KEY = 'aurexis_session_id';
  const currentSessionIdRef = useRef<string>('');
  const [sessionLog, setSessionLog] = useState<SessionLogEntry[]>([]);
  const [showSessionHistory, setShowSessionHistory] = useState(false);

  useEffect(() => {
    const t = setVisibleInterval(() => {
      const stored = sessionStorage.getItem(SESSION_START_KEY);
      if (stored) {
        setSessSecs(Math.floor((Date.now() - Number(stored)) / 1000));
      }
    }, 1000);
    return () => t();
  }, []);

  useEffect(() => {
    // Append this session's start record exactly once per tab lifetime.
    // Guard via sessionStorage (SESSION_ID_KEY): if this tab already logged
    // a start (e.g. this effect re-running because the user routed away
    // from and back to this dashboard within the SPA), reuse that same
    // entry instead of appending a duplicate "session" that never happened.
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    const existingLog = readSessionLog();
    if (id && existingLog.some(e => e.id === id)) {
      currentSessionIdRef.current = id;
    } else {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(SESSION_ID_KEY, id);
      currentSessionIdRef.current = id;
      writeSessionLog([...existingLog, { id, startedAt: new Date().toISOString(), endedAt: null }]);
    }
    setSessionLog(readSessionLog());

    const closeCurrentSession = () => {
      const log = readSessionLog();
      const idx = log.findIndex(e => e.id === currentSessionIdRef.current);
      if (idx === -1) return;
      if (log[idx].endedAt) return; // already closed once — never overwrite
      log[idx] = { ...log[idx], endedAt: new Date().toISOString() };
      writeSessionLog(log);
    };

    // beforeunload + pagehide both fire on a real tab/window close.
    // pagehide is the more reliable of the two — it also fires when a page
    // becomes bfcache-eligible, a case some browsers skip beforeunload for.
    // visibilitychange is deliberately NOT used: it also fires on an
    // ordinary tab switch (tab stays open), which would wrongly mark a
    // still-active session as ended.
    window.addEventListener('beforeunload', closeCurrentSession);
    window.addEventListener('pagehide', closeCurrentSession);
    return () => {
      window.removeEventListener('beforeunload', closeCurrentSession);
      window.removeEventListener('pagehide', closeCurrentSession);
    };
  }, []);

  const fmtSecs = (s: number) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  };

  const fmtSessionDuration = (startedAt: string, endedAt: string | null) => {
    const start = new Date(startedAt).getTime();
    const end = endedAt ? new Date(endedAt).getTime() : Date.now();
    return fmtSecs(Math.max(0, Math.floor((end - start) / 1000)));
  };

  const openSessionHistory = () => {
    setSessionLog(readSessionLog()); // pick up anything written since mount
    setShowSessionHistory(true);
  };

  const sortedSessionLog = [...sessionLog].reverse(); // most recent first

  return {
    sessSecs, sessionLog: sortedSessionLog, currentSessionId: currentSessionIdRef.current,
    showSessionHistory, setShowSessionHistory, openSessionHistory, fmtSecs, fmtSessionDuration,
  };
}
